import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Lang } from '@/data/types';
import { KBIcon } from '@/components/kbIcons';
import { friendlyError } from '@/lib/friendlyError';
import { actesEn } from '@/lib/actesCountry';

/**
 * VOIR LE DOCUMENT — sans quitter l'écran.
 *
 * Opening a new browser tab for every scan was my shortcut, not the original's
 * behaviour, and it is worse in the one place it matters: checking a
 * certificate against the row you are reading means holding both at once. A
 * PDF renders in a frame, an image renders as an image, and the download stays
 * one press away for the times you really do want the file.
 *
 * One component, three sources — the act received on a demande, a document
 * attached to it, and a scan from the reception log — because they differ only
 * in which signed link to ask for. The caller passes that as a loader.
 */
export function DocViewer({ lang, load, onClose }: {
  lang: Lang;
  /** Asks the server for the short-lived link, its name and its type. */
  load: () => Promise<{ url: string; downloadUrl?: string; name: string | null; mime?: string | null }>;
  onClose: () => void;
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const [state, setState] = useState<{ url: string; downloadUrl: string; name: string; mime: string } | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let live = true;
    load()
      .then((f) => {
        if (!live) return;
        setState({
          url: f.url,
          downloadUrl: f.downloadUrl || f.url,
          name: f.name || 'document',
          mime: f.mime || '',
        });
      })
      .catch((e) => { if (live) setErr(friendlyError(e, lang)); });
    return () => { live = false; };
    // The loader is created per open; re-running on its identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Escape closes it — a viewer you cannot dismiss with the keyboard is a
     viewer people stop opening. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const isImage = (state?.mime || '').startsWith('image/');

  return createPortal(
    <div className="actes-viewer-back actes-skin" onClick={onClose}>
      <div className="actes-viewer" onClick={(e) => e.stopPropagation()}>
        <div className="actes-viewer-h">
          <KBIcon name="documents" size={14} />
          <span className="actes-viewer-n">{state?.name || T('Document', 'Document')}</span>
          {state && (
            <a className="actes-row-act" href={state.downloadUrl} download={state.name}
              title={T('Télécharger', 'Download')}>
              <KBIcon name="download" size={14} />
            </a>
          )}
          <button className="actes-row-act" onClick={onClose} aria-label={T('Fermer', 'Close')}>
            <KBIcon name="x" size={14} />
          </button>
        </div>
        <div className="actes-viewer-b">
          {err ? <p className="muted-note ni-err">⚠ {err}</p>
            : !state ? <p className="muted-note">{T('Chargement…', 'Loading…')}</p>
              : isImage ? <img src={state.url} alt={state.name} />
                : <iframe src={state.url} title={state.name} />}
        </div>
      </div>
    </div>,
    document.body,
  );
}
